import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'
import { generatePassageItinerary } from '@/services/ai'
import { getDestinationImages, fetchImages, type Img } from '@/services/images'
import { FallbackImage } from '@/components/shared/FallbackImage'
import { Attribution } from '@/components/shared/Attribution'
import { openDeal, NETWORK_LABELS } from '@/services/affiliates'
import type { Destination, Stop } from '@/types'

const DAY_OPTIONS = [2, 3, 4, 5, 6]

interface DayPlan {
  day: number
  title: string
  highlight: string
  stops: Stop[]
}

// Build a curated fallback narrative from the destination description — used
// whenever Claude is unavailable, so the screen is never blank.
function curatedNarrative(dest: Destination, days: number): string {
  const flavour = dest.description.split('. ')[0]
  return `${flavour}. Over ${days} unhurried ${days === 1 ? 'day' : 'days'}, Passage has arranged ${dest.name} at a pace befitting a traveller who has earned the right to linger. Every stop below has been chosen for depth, not haste.`
}

// Reshape the destination's curated stops into exactly `days` day-cards,
// cycling through the stop list if there are more days than stops.
function curatedDayPlan(dest: Destination, days: number): DayPlan[] {
  const stops = dest.stops.length > 0 ? dest.stops : [
    { time: 'All day', name: dest.name, description: dest.description, curatorNote: '✦ A day of quiet discovery' },
  ]
  const perDay = Math.max(1, Math.ceil(stops.length / days))
  return Array.from({ length: days }, (_, i) => {
    const daySlice = stops.slice(i * perDay, i * perDay + perDay)
    const fallbackStop = stops[i % stops.length]
    const dayStops = daySlice.length > 0 ? daySlice : [fallbackStop]
    return {
      day: i + 1,
      title: dayStops[0]?.name || `Day ${i + 1} in ${dest.name}`,
      highlight: dayStops[0]?.description?.split('. ')[0] || `A considered day exploring ${dest.name}.`,
      stops: dayStops,
    }
  })
}

// Merge Claude's {day,title,highlight} onto the destination's real stops so
// imagery/offers still have something concrete to render against.
function mapGeneratedToStops(dest: Destination, generated: { day: number; title: string; highlight: string }[]): DayPlan[] {
  const stops = dest.stops.length > 0 ? dest.stops : [
    { time: 'All day', name: dest.name, description: dest.description, curatorNote: '✦ A day of quiet discovery' },
  ]
  return generated.map((g, i) => ({
    day: g.day ?? i + 1,
    title: g.title || dest.stops[i % stops.length]?.name || `Day ${i + 1}`,
    highlight: g.highlight || `A day of discovery in ${dest.name}.`,
    stops: [stops[i % stops.length]],
  }))
}

export function FullItineraryScreen() {
  const { selectedDestination, dnaKey, setScreen } = useAppStore()
  const profile = DNA_PROFILES[dnaKey]
  const dest = selectedDestination ?? (DESTINATIONS[dnaKey]?.[0] || null)

  const naturalDays = useMemo(() => {
    if (!dest) return 3
    const n = dest.stops.length || 3
    return Math.min(6, Math.max(2, n))
  }, [dest])

  const [days, setDays] = useState<number>(naturalDays)
  const [narrative, setNarrative] = useState('')
  const [aiUsed, setAiUsed] = useState(false)
  const [dayPlan, setDayPlan] = useState<DayPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [imagesByStop, setImagesByStop] = useState<Record<string, Img[]>>({})
  const [galleryImages, setGalleryImages] = useState<Img[]>([])

  // Regenerate the day plan whenever the day count or destination changes.
  useEffect(() => {
    if (!dest || !profile) return
    let cancelled = false
    setLoading(true)

    generatePassageItinerary(dest.name, profile.name, days)
      .then((generated) => {
        if (cancelled) return
        const isReal = Array.isArray(generated) && generated.length > 0 &&
          generated.some((g) => g?.title && !/^Day \d+$/.test(g.title))
        if (isReal) {
          setDayPlan(mapGeneratedToStops(dest, generated))
          setNarrative(`Your perfect ${days} days in ${dest.name} begin the moment you decide to stop rushing. ${dest.description}`)
          setAiUsed(true)
        } else {
          setDayPlan(curatedDayPlan(dest, days))
          setNarrative(curatedNarrative(dest, days))
          setAiUsed(false)
        }
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setDayPlan(curatedDayPlan(dest, days))
        setNarrative(curatedNarrative(dest, days))
        setAiUsed(false)
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [dest?.id, profile?.name, days])

  // Destination-wide gallery, round-robined across day/stop imagery.
  useEffect(() => {
    if (!dest) return
    let cancelled = false
    getDestinationImages(dest, 6).then((imgs) => { if (!cancelled) setGalleryImages(imgs) })
    return () => { cancelled = true }
  }, [dest?.id])

  // Per-stop imagery, best-effort — falls back to gallery round-robin, then gradient.
  useEffect(() => {
    if (!dest || dayPlan.length === 0) return
    let cancelled = false
    dayPlan.forEach(async (d) => {
      const stop = d.stops[0]
      if (!stop) return
      const key = `${d.day}-${stop.name}`
      if (imagesByStop[key]) return
      try {
        const imgs = await fetchImages(`${stop.name} ${dest.name}`, 2)
        if (!cancelled && imgs.length > 0) setImagesByStop((prev) => ({ ...prev, [key]: imgs }))
      } catch { /* silent — gradient fallback used */ }
    })
    return () => { cancelled = true }
  }, [dayPlan, dest?.id])

  if (!dest || !profile) { setScreen('journey'); return null }

  const stopImageFor = (d: DayPlan, idx: number): Img | undefined => {
    const key = `${d.day}-${d.stops[0]?.name}`
    const specific = imagesByStop[key]?.[0]
    if (specific) return specific
    if (galleryImages.length > 0) return galleryImages[idx % galleryImages.length]
    return undefined
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: 'radial-gradient(ellipse at 50% 0%, #33200F 0%, #241209 55%, #180C05 100%)',
      position: 'relative',
    }}>
      <Atmosphere count={16} />
      <div style={{ position: 'relative', zIndex: 5 }}>
        <Topbar center={`${dest.name} · Full Itinerary`} backTo="card" backLabel="← Journey Card" />
      </div>

      <div style={{ width: '100%', maxWidth: 760, margin: '0 auto', padding: '30px 22px 60px', position: 'relative', zIndex: 5 }}>

        {/* Hierarchy label */}
        <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.12em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 10 }}>
          {dest.flag} {dest.country ? `${dest.country} › ` : ''}<span style={{ color: 'var(--gold2)' }}>{dest.name}</span>
        </div>

        {/* Header */}
        <motion.h1 initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          style={{ fontFamily: 'var(--serif-display)', fontSize: 'clamp(26px, 4.6vw, 40px)', color: '#F5EDD8', lineHeight: 1.2, marginBottom: 14 }}>
          Your perfect <em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>{days} days</em> in {dest.name}
        </motion.h1>

        {/* Narrative intro */}
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 40, marginBottom: 6 }}>
            {[0, 1, 2].map((i) => (
              <motion.div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)' }}
                animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.24 }} />
            ))}
            <span style={{ fontFamily: 'var(--serif-body)', fontSize: 13, color: 'rgba(245,237,216,0.6)', fontStyle: 'italic' }}>
              Passage is composing your itinerary…
            </span>
          </div>
        ) : (
          <motion.p key={narrative} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            style={{ fontFamily: 'var(--serif-body)', fontSize: 15.5, color: 'rgba(245,237,216,0.75)', lineHeight: 1.8, maxWidth: 620, marginBottom: 12, fontStyle: 'italic' }}>
            {narrative}
          </motion.p>
        )}

        {!loading && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.25)', padding: '3px 10px', marginBottom: 22 }}>
            <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.12em', color: 'var(--gold2)', textTransform: 'uppercase' }}>
              {aiUsed ? '✦ Personalised by Claude AI' : '✦ CACHED · Curated by Passage'}
            </span>
          </div>
        )}

        {/* Day-count selector */}
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.14em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 10 }}>
            How many days would you like to spend?
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {DAY_OPTIONS.map((n) => (
              <button key={n} onClick={() => setDays(n)} style={{
                padding: '8px 18px', borderRadius: 999,
                border: days === n ? '1.5px solid var(--gold)' : '1px solid rgba(212,160,23,0.25)',
                background: days === n ? 'linear-gradient(90deg, var(--gold), var(--gold2))' : 'rgba(255,255,255,0.03)',
                color: days === n ? '#241209' : 'rgba(245,237,216,0.7)',
                fontFamily: 'var(--serif-caps)', fontSize: 12, letterSpacing: '0.06em', cursor: 'pointer',
                transition: 'all 0.25s ease',
              }}>
                {n} {n === 1 ? 'day' : 'days'}
              </button>
            ))}
          </div>
        </div>

        {/* Day-by-day list */}
        <AnimatePresence mode="wait">
          <motion.div key={days} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 30 }}>
            {dayPlan.map((d, idx) => {
              const img = stopImageFor(d, idx)
              return (
                <motion.div key={d.day} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08, duration: 0.5 }}
                  className="p-glow-card"
                  style={{ border: '1px solid rgba(212,160,23,0.22)', background: 'rgba(255,255,255,0.025)', overflow: 'hidden' }}>

                  <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 0 }}>
                    {/* Photo */}
                    <FallbackImage
                      src={img?.thumb || img?.url}
                      alt={d.stops[0]?.name || d.title}
                      style={{ minHeight: 150, height: '100%' }}
                      fallback={
                        <div style={{ minHeight: 150, height: '100%', background: dest.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>
                          {dest.emoji}
                        </div>
                      }
                    />

                    <div style={{ padding: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                        <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.12em', color: 'var(--gold2)', textTransform: 'uppercase' }}>
                          Day {d.day}
                        </span>
                        <span style={{ fontFamily: 'var(--serif-display)', fontSize: 18, color: '#F5EDD8', fontStyle: 'italic' }}>{d.title}</span>
                      </div>
                      <div style={{ fontFamily: 'var(--serif-body)', fontSize: 13, color: 'rgba(245,237,216,0.72)', lineHeight: 1.6, marginBottom: 10 }}>
                        {d.highlight}
                      </div>

                      {/* Stops */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {d.stops.map((stop, si) => (
                          <div key={si} style={{ borderTop: '1px solid rgba(212,160,23,0.15)', paddingTop: 8 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.08em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase' }}>⏱ {stop.time}</span>
                              <span style={{ fontFamily: 'var(--serif-display)', fontSize: 14, color: '#F5EDD8' }}>{stop.name}</span>
                            </div>
                            <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'rgba(245,237,216,0.62)', lineHeight: 1.6, marginTop: 3 }}>
                              {stop.description}
                            </div>
                            {stop.curatorNote && (
                              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, color: 'var(--gold2)', letterSpacing: '0.04em', marginTop: 4 }}>
                                {stop.curatorNote}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {img?.photographer && <div style={{ marginTop: 8 }}><Attribution img={img} dark /></div>}

                      {/* One relevant partner offer woven per day, cycling through the destination's deals */}
                      {dest.deals.length > 0 && (
                        <button onClick={() => openDeal(dest.deals[idx % dest.deals.length], dest.name)}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 10, marginTop: 12,
                            padding: '9px 12px', background: 'rgba(212,160,23,0.07)', border: '1px solid rgba(212,160,23,0.28)',
                            cursor: 'pointer', textAlign: 'left',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(212,160,23,0.14)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(212,160,23,0.07)')}
                        >
                          <span style={{ fontSize: 15 }}>{dest.deals[idx % dest.deals.length].icon}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: 'var(--serif-display)', fontSize: 12.5, color: '#F5EDD8' }}>{dest.deals[idx % dest.deals.length].title}</div>
                            <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'rgba(245,237,216,0.5)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                              via {NETWORK_LABELS[dest.deals[idx % dest.deals.length].network] || dest.deals[idx % dest.deals.length].network}
                            </div>
                          </div>
                          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid rgba(212,160,23,0.4)', color: 'var(--gold2)', whiteSpace: 'nowrap' }}>
                            {dest.deals[idx % dest.deals.length].saving}
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        </AnimatePresence>

        {/* Partner offers block for the trip */}
        {dest.deals.length > 0 && (
          <div className="p-glow-card" style={{ border: '1px solid rgba(212,160,23,0.22)', background: 'rgba(255,255,255,0.025)', marginBottom: 30 }}>
            <div style={{ padding: '14px 18px 6px', fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase' }}>
              ✦ Partner Offers for This Trip
            </div>
            <div style={{ paddingBottom: 6 }}>
              {dest.deals.map((deal, i) => (
                <button key={i} onClick={() => openDeal(deal, dest.name)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px',
                    background: 'transparent', border: 'none', borderBottom: i < dest.deals.length - 1 ? '1px solid rgba(212,160,23,0.15)' : 'none',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(212,160,23,0.06)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,160,23,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>{deal.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--serif-display)', fontSize: 13, color: '#F5EDD8' }}>{deal.title}</div>
                    <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(245,237,216,0.58)', marginTop: 1 }}>{deal.detail}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid rgba(212,160,23,0.4)', color: 'var(--gold2)', whiteSpace: 'nowrap' }}>{deal.saving}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => setScreen('card')} style={{
            flex: 1, minWidth: 200, padding: 14, background: 'linear-gradient(90deg, var(--gold), var(--gold2))', border: 'none',
            color: '#241209', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.12em', cursor: 'pointer', textTransform: 'uppercase',
          }}>
            ← Back to Journey Card
          </button>
          <button onClick={() => setScreen('card')} style={{
            flex: 1, minWidth: 200, padding: 14, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,160,23,0.3)',
            color: 'rgba(245,237,216,0.75)', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.12em', cursor: 'pointer', textTransform: 'uppercase',
          }}>
            Send to Phone
          </button>
        </div>
      </div>
    </div>
  )
}
